import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_NODE_URL,
  BULK_STORE_SETTINGS_SCHEMA,
  BULK_STORE_SETTINGS_CURRENT_VALUES,
  BULK_STORE_SETTINGS_SUBMIT,
  BULK_STORE_SETTINGS_JOBS,
  BULK_STORE_SETTINGS_JOB_DETAIL,
  BULK_STORE_SETTINGS_CANCEL,
} from "../../../Constants/Config";
import { getAuthDetails } from "../../../Common/cookiesHelper";

// Store Settings > Bulk Update. The Node API decides which settings this
// session may see (superadmin-only ones are never returned to anyone else)
// from the verified token, so the page renders whatever /schema returns.
const initialState = {
  schema: [],
  maxStores: 0,
  schemaLoading: false,
  schemaError: "",
  currentValues: {},
  currentValuesLoading: false,
  currentValuesError: "",
  submitLoading: false,
  jobs: [],
  jobsCount: 0,
  jobsLoading: false,
  jobDetail: null,
  jobDetailLoading: false,
};

const nodePost = async (endpoint, data, rejectWithValue) => {
  // getAuthDetails is the plain (non-hook) cookie reader — safe outside React.
  const { userTypeData } = getAuthDetails();
  const { token, ...otherUserData } = userTypeData;
  try {
    const response = await axios.post(
      BASE_NODE_URL + endpoint,
      { ...data, ...otherUserData },
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue({
      // Controllers answer { message }; the auth middleware answers { error }.
      message:
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Request failed",
      status: toThunkRejectValue(error).status,
      data: error.response ? error.response.data : null,
    });
  }
};

export const fetchBulkSettingsSchema = createAsyncThunk(
  "bulkStoreSettings/schema",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_SCHEMA, data, rejectWithValue),
);

export const fetchBulkSettingsCurrentValues = createAsyncThunk(
  "bulkStoreSettings/currentValues",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_CURRENT_VALUES, data, rejectWithValue),
);

export const submitBulkSettings = createAsyncThunk(
  "bulkStoreSettings/submit",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_SUBMIT, data, rejectWithValue),
);

export const fetchBulkSettingsJobs = createAsyncThunk(
  "bulkStoreSettings/jobs",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_JOBS, data, rejectWithValue),
);

export const fetchBulkSettingsJobDetail = createAsyncThunk(
  "bulkStoreSettings/jobDetail",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_JOB_DETAIL, data, rejectWithValue),
);

export const cancelBulkSettingsJob = createAsyncThunk(
  "bulkStoreSettings/cancel",
  async (data, { rejectWithValue }) =>
    nodePost(BULK_STORE_SETTINGS_CANCEL, data, rejectWithValue),
);

const bulkStoreSettingsSlice = createSlice({
  name: "bulkStoreSettings",
  initialState,
  reducers: {
    clearBulkSettingsJobDetail(state) {
      state.jobDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBulkSettingsSchema.pending, (state) => {
        state.schemaLoading = true;
        state.schemaError = "";
      })
      .addCase(fetchBulkSettingsSchema.fulfilled, (state, action) => {
        state.schemaLoading = false;
        if (action.payload?.status) {
          state.schema = action.payload.sections || [];
          state.maxStores = Number(action.payload.max_stores) || 0;
        } else {
          state.schemaError =
            action.payload?.message || "Could not load settings";
        }
      })
      .addCase(fetchBulkSettingsSchema.rejected, (state, action) => {
        state.schemaLoading = false;
        state.schemaError =
          action.payload?.message || "Could not load settings";
      })
      .addCase(fetchBulkSettingsCurrentValues.pending, (state) => {
        state.currentValuesLoading = true;
        state.currentValuesError = "";
      })
      .addCase(fetchBulkSettingsCurrentValues.fulfilled, (state, action) => {
        state.currentValuesLoading = false;
        if (action.payload?.status) {
          state.currentValues = action.payload.values || {};
        } else {
          state.currentValuesError =
            action.payload?.message || "Could not load this store's settings";
        }
      })
      .addCase(fetchBulkSettingsCurrentValues.rejected, (state, action) => {
        state.currentValuesLoading = false;
        state.currentValuesError =
          action.payload?.message || "Could not load this store's settings";
      })
      .addCase(submitBulkSettings.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(submitBulkSettings.fulfilled, (state) => {
        state.submitLoading = false;
      })
      .addCase(submitBulkSettings.rejected, (state) => {
        state.submitLoading = false;
      })
      .addCase(fetchBulkSettingsJobs.pending, (state) => {
        state.jobsLoading = true;
      })
      .addCase(fetchBulkSettingsJobs.fulfilled, (state, action) => {
        state.jobsLoading = false;
        if (action.payload?.status) {
          state.jobs = action.payload.result || [];
          state.jobsCount = Number(action.payload.total_count) || 0;
        }
      })
      .addCase(fetchBulkSettingsJobs.rejected, (state) => {
        state.jobsLoading = false;
      })
      .addCase(fetchBulkSettingsJobDetail.pending, (state) => {
        state.jobDetailLoading = true;
      })
      .addCase(fetchBulkSettingsJobDetail.fulfilled, (state, action) => {
        state.jobDetailLoading = false;
        if (action.payload?.status) {
          state.jobDetail = {
            job: action.payload.job,
            stores: action.payload.stores || [],
          };
        }
      })
      .addCase(fetchBulkSettingsJobDetail.rejected, (state) => {
        state.jobDetailLoading = false;
      });
  },
});

export const { clearBulkSettingsJobDetail } = bulkStoreSettingsSlice.actions;
export default bulkStoreSettingsSlice.reducer;
